import { getWebPath } from '@doss/common-all'
import Bluebird, { all as promiseAll } from 'bluebird'
import { useField, useFormikContext } from 'formik'
import { compact, debounce } from 'lodash'
import { Children, MouseEvent, useEffect, useState } from 'react'
import { RxPlus } from 'react-icons/rx'
import { FileChip } from '~/@/components/ui/file-chip'
import {
  BreadcrumbObjectType,
  useCreateFileCellDocumentViewMutation,
  useDeleteDocumentMutation,
  useGetFileCellDocumentLazyQuery,
} from '~/common/generated/apollo-operations'
import { useDisclosure } from '~/common/hooks/useDisclosure'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { getDropzoneAcceptOptionForAllDocumentTypes } from '~/common/utils/document'
import { DropzoneDialog } from '~/components/dropzone/DropzoneDialog'
import { FileCellDocument } from '~/components/records/record-cell-details/types'
import { AddRecordFormCell, RecordDetailTableCell } from '~/components/records/types'
import { useDossSnackbar } from '~/components/shared/DossSnackbar'
import { FileInlineImage } from '~/components/table-cells/FileTableCells/FileInlineImage'
import { SignatureChip } from '../../../ui/molecules/file-chip/SignatureChip'
import { useRecordDetailsCellPermissions } from '../../RecordDetailsCellPermissionsProvider'

interface FileArrayCellDetailsProps {
  cell: RecordDetailTableCell | AddRecordFormCell
  addDocumentId?: (documentId: string) => void
  removeDocumentId?: (documentId: string) => void
}

/**
 * Renders the record details for a FILE cell with column of type ARRAY
 */
export function FileArrayCellDetails({
  cell,
  addDocumentId,
  removeDocumentId,
}: FileArrayCellDetailsProps) {
  const { t } = useTypedTranslation()
  const snackbar = useDossSnackbar()
  const { isReadOnly } = useRecordDetailsCellPermissions()
  const [createFileCellDocumentView] = useCreateFileCellDocumentViewMutation({
    onCompleted({ createDocumentView }) {
      if (addDocumentId) {
        addDocumentId(createDocumentView.id)
      }
    },
  })
  // Value is undefined if there are no documents on the cell and the user is a non-admin user
  const [{ value }, { initialValue }, { setValue, setTouched }] = useField<string[] | undefined>(
    cell.column.id,
  )
  const formikContext = useFormikContext()
  const [isUploading, setIsUploading] = useState(false)
  const [getFile] = useGetFileCellDocumentLazyQuery()
  const [documents, setDocuments] = useState<FileCellDocument[]>([])
  const dropzoneDisclosure = useDisclosure()
  const [deleteDocument] = useDeleteDocumentMutation({
    update(cache, { data }) {
      if (!data) {
        return
      }

      const normalizedId = cache.identify(data.deleteDocument)
      cache.evict({ id: normalizedId })
      cache.gc()
    },
  })

  useEffect(() => {
    const getAllFiles = async (documentIds: string[]) => {
      return Bluebird.map(documentIds, (documentId) => getFile({ variables: { documentId } }))
    }

    getAllFiles(compact(value)).then((results) => {
      setDocuments(compact(results.map((result) => result.data?.document)))
    })
  }, [value, cell.documents, getFile])

  const handleFileDelete = async (
    event: MouseEvent<HTMLButtonElement, Event>,
    uploadedFileId: string,
  ) => {
    event.preventDefault()
    // Delete the document if it is not one of the original documents on the cell
    if (!initialValue?.some((id) => id === uploadedFileId)) {
      await deleteDocument({ variables: { documentId: uploadedFileId } })
      if (removeDocumentId) {
        removeDocumentId(uploadedFileId)
      }
    }
    setValue(value?.filter((documentId) => documentId !== uploadedFileId))
    setTouched(true)
    debouncedValidation()
  }

  const displayImageInline = cell.column.displayImagesInline

  // Debounce validation call
  const debouncedValidation = debounce(() => {
    formikContext.validateField(cell.column.id)
  }, 0)

  const fileContent = (file: FileCellDocument) => {
    if (file.signature) {
      return <SignatureChip document={file} signature={file.signature} />
    } else if (displayImageInline) {
      return (
        <FileInlineImage
          document={file}
          onDelete={isReadOnly ? undefined : (event) => handleFileDelete(event, file.id)}
        />
      )
    } else {
      const fileUrl = getWebPath({
        objectId: file.id,
        objectType: BreadcrumbObjectType.DOCUMENT,
      })

      return (
        <FileChip
          disabled={isReadOnly}
          document={file}
          fileName={file.name}
          fileUrl={fileUrl}
          onDelete={isReadOnly ? undefined : (event) => handleFileDelete(event, file.id)}
        />
      )
    }
  }

  return (
    <div className="flex flex-wrap gap-2">
      {Children.toArray(documents.map((uploadedFile) => <div>{fileContent(uploadedFile)}</div>))}
      <div>
        {!isReadOnly && (
          <button
            className="flex min-h-6 items-center gap-1.5 ps-1 text-xs-tall font-normal text-gray-10 hover:text-blue-9"
            onClick={(e) => {
              e.preventDefault()
              dropzoneDisclosure.open()
            }}
            tabIndex={0}
            type="button"
          >
            <RxPlus />
            {t('common.upload_file')}
          </button>
        )}
      </div>
      {dropzoneDisclosure.isOpen && (
        <DropzoneDialog
          allowMultiple={true}
          dialogProps={{
            open: dropzoneDisclosure.isOpen,
          }}
          dropzoneOptions={{
            accept: getDropzoneAcceptOptionForAllDocumentTypes(),
          }}
          isUploading={isUploading}
          onCancel={dropzoneDisclosure.close}
          onDone={async (files) => {
            setIsUploading(true)
            try {
              const uploadedDocuments = await promiseAll(
                files.map((file) =>
                  createFileCellDocumentView({
                    variables: {
                      input: {
                        name: file.name,
                        file,
                      },
                    },
                  }),
                ),
              )
              const newDocumentIds = compact(
                uploadedDocuments.map((result) => result.data?.createDocumentView.id),
              )
              setValue(compact([...(value ?? []), ...newDocumentIds]))
              setTouched(true)
              setIsUploading(false)
              // Explicitly trigger field validation using Formik's context.
              // This call is debounced to optimize performance and ensure the validation logic
              // executes only after other state updates have been processed.
              debouncedValidation()
              dropzoneDisclosure.close()
              return
            } catch (err) {
              setIsUploading(false)
              snackbar.showError(`Failed to upload: ${err.message}`)
            }
          }}
        />
      )}
    </div>
  )
}
